import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Button,
    CircularProgress, FormControl, IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Snackbar,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import Table from "../../components/Table/Table";
import { useDeleteUserMutation, useGetUsersQuery, useUpdateUserMutation, useExportUsersMutation } from "../../store/services/user-service";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import IosShareIcon from '@mui/icons-material/IosShare';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/Email';
import AddUserModal from "./Modal/AddUserModal";
import ImportCsvModal from "./Modal/ImportCsvModal";
import DeleteCsvModal from "./Modal/DeleteCsvModal";
import UpdateEmailCsvModal from "./Modal/UpdateEmailCsvModal";
import AssignAdminModal from "./Modal/AssignAdminModal";
import { useSendTenancyReminderEmailMutation } from "../../store/services/response.service";
import { useGetSchedulerSettingsQuery } from "../../store/services/scheduler.service";
import { useCreateOnDemandMutation } from "../../store/services/tenancy.service";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import ConfirmationDialog from "../../utils/confirmationDialog";
import UserEnableDisable from "../../components/Button/UserEnableDisable";
import {
    useSendManualCourseMutation,
    useSendManualContentPackMutation,
    useSendTenancyContentPackMutation
} from "../../store/services/university-service";
import { useUpdateUserRoleMutation,
    useAddUserMutation
} from '../../store/services/user-service';
import { USER_SOURCE_LABELS, resolveUserSourceLabel } from "./userSource";

UsersTable.propTypes = {
    type: PropTypes.oneOf(["edit", "select", "filter", "send", "selectOne"]).isRequired,
    userIds: PropTypes.array,
    setUserId: PropTypes.func,
    filterBy: PropTypes.string,
    filterId: PropTypes.string,
    formatId: PropTypes.string,
    format: PropTypes.string,
    contentPack: PropTypes.object,
    CSVButton: PropTypes.bool
};

export default function UsersTable(props) {
    const { type, userIds, setUserId, filterBy, filterId, formatId, format, contentPack} = props;
    const theme = useTheme();
    const navigate = useNavigate();
    const { tenancyId } = useSelector((state) => state.tenancy);
    // BD-775: the tenancy-wide reminder send is disabled while emails are paused for this tenancy.
    const { data: schedulerSettings } = useGetSchedulerSettingsQuery({ tenancyId }, { skip: !tenancyId });
    const emailsPaused = schedulerSettings?.pauseSending === true;
    const [searchParams] = useSearchParams();
    const userDepartmentId = searchParams.get('userDepartmentId');
    const userLocationId = searchParams.get('userLocationId');
    const userCityId = searchParams.get('userCityId');
    const userCountryId = searchParams.get('userCountryId');
    const userStateId = searchParams.get('userStateId');
    const userCompanyNameId = searchParams.get('userCompanyNameId');

    const lightMode = theme.palette.mode === "light";
    const matchDownMD = useMediaQuery(theme.breakpoints.down("md"));
    const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));

    const userLoggedIn = JSON.parse(localStorage.getItem("user"));
    const userRole = userLoggedIn?.role || '';
    const isAdmin = userRole === 'Administrators';
    const isTenancyAdmin = userRole.includes('TenancyAdministrators');

    const [exportUsers] = useExportUsersMutation();
    const [deleteUser] = useDeleteUserMutation();
    const [updateUser] = useUpdateUserMutation();
    const [addUser] = useAddUserMutation();

    const [updateUserRole] = useUpdateUserRoleMutation();
    const [sendTenancyReminderEmail] = useSendTenancyReminderEmailMutation();
    const [createOnDemand] = useCreateOnDemandMutation();
    const [sendManualCourse] = useSendManualCourseMutation();
    const [sendManualContentPack] = useSendManualContentPackMutation();
    const [sendTenancyContentPack] = useSendTenancyContentPackMutation();

    const [statusFilter, setStatusFilter] = useState("All");
    const [roleFilter, setRoleFilter] = useState("All");
    const [sourceFilter, setSourceFilter] = useState("All");
    const [serverSearchQuery, setServerSearchQuery] = useState("");
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    const [visibleColumns, setVisibleColumns] = useState([]);

    const isRoleColumnVisible = () => {
        const isVisible = visibleColumns.includes('Role');
        return isVisible;
    };

    const queryParams = useMemo(() => {
        const params = {
            perPage: paginationModel.pageSize,
            page: paginationModel.page,
            includes: "",
            query: "",
            searchTerm: serverSearchQuery,
            tenancyId: tenancyId,
            adminFilter: false,
            extendedResults: true
        };

        if (userDepartmentId) {
            params.departmentId = parseInt(userDepartmentId);
        }

        if (userLocationId) {
            params.locationId = parseInt(userLocationId);
        }

        if (userCityId) {
            params.cityId = parseInt(userCityId);
        }

        if (userCountryId) {
            params.countryId = parseInt(userCountryId);
        }

        if (userStateId) {
            params.stateId = parseInt(userStateId);
        }

        if (userCompanyNameId) {
            params.companyNameId = parseInt(userCompanyNameId);
        }

        if (roleFilter !== "All") {
            params.roleFilter = roleFilter;
        }

        if (sourceFilter !== "All") {
            params.sourceFilter = sourceFilter;
        }

        if (statusFilter === "Enabled") {
            params.enabledFilter = true;
        } else if (statusFilter === "Disabled") {
            params.enabledFilter = false;
        }

        return params;
    }, [paginationModel, tenancyId, serverSearchQuery, userDepartmentId, userLocationId, userCityId, userCountryId, userStateId, userCompanyNameId, roleFilter, sourceFilter, statusFilter]);

    const {
        data: usersData,
        error: usersError,
        isFetching: usersLoading,
        refetch
    } = useGetUsersQuery(queryParams);

    const getChangedData = (newPaginationModel) => {
        setPaginationModel(newPaginationModel);
    };

    const totalUsers = usersData?.rowsAffected;

    const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);
    const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [importCsvModalOpen, setImportCsvModalOpen] = useState(false);
    const [deleteCsvModalOpen, setDeleteCsvModalOpen] = useState(false);
    const [updateEmailCsvModalOpen, setUpdateEmailCsvModalOpen] = useState(false);
    const [isExportingAll, setIsExportingAll] = useState(false);
    const [isExportingActive, setIsExportingActive] = useState(false);
    const [selected, setSelected] = useState([]);
    const [filteredUsersData, setFilteredUsersData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSendingReminders, setIsSendingReminders] = useState(false);
    const [isSendingToTenancy, setIsSendingToTenancy] = useState(false);
    const [userLoading, setUserLoading] = useState({});
    const [isAdminsModalOpen, setIsAdminsModalOpen] = useState(false);
    const [adminFormState, setAdminFormState] = useState({
    assignAdmins: {
        admins: []
    }
});

    // Confirmation Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);

    const renderUserButton = (user) => (
        <UserEnableDisable
            user={user}
            type={type}
            handleEnableChange={handleEnableChange}
            userLoading={userLoading}
        />
    );

    const getFilteredUsers = () => {
        if (type === "filter") {
            return filteredUsersData;
        }

        if (!usersData?.data) {
            return [];
        }

        let filteredData = usersData.data;

        if (statusFilter === "Enabled") {
            filteredData = filteredData.filter(user => user.enabled);
        } else if (statusFilter === "Disabled") {
            filteredData = filteredData.filter(user => !user.enabled);
        }

        return filteredData;
    };

    const handleBulkMenuClick = (event) => {
        setBulkMenuAnchorEl(event.currentTarget);
        setBulkMenuOpen(true);
    };

    const handleBulkMenuClose = () => {
        setBulkMenuAnchorEl(null);
        setBulkMenuOpen(false);
    };

    const handleAddUserManually = () => {
        setAddUserModalOpen(true);
    };

    const handleExportUsers = async (onlyActive) => {
        onlyActive ? setIsExportingActive(true) : setIsExportingAll(true);
        try {
            const response = await exportUsers({
                tenancyId,
                onlyActive,
            }).unwrap();

            if (response && response.preSignedUrl) {
                const downloadInIframe = (url) => {
                    const iframe = document.createElement('iframe');
                    iframe.style.display = 'none';
                    iframe.src = url;
                    document.body.appendChild(iframe);

                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 2000);
                };

                const checkFileReady = async () => {
                    try {
                        const result = await fetch(response.preSignedUrl, {
                            method: 'GET',
                            headers: { skipInterceptor: 'true' }
                        });
                        if (result.ok) {
                            downloadInIframe(response.preSignedUrl);
                            return true;
                        }
                        return false;
                    } catch (error) {
                        return false;
                    }
                };

                let attempts = 0;
                while (attempts < 15) {
                    const isReady = await checkFileReady();
                    if (isReady) {
                        setSnackbarMessage("Users exported successfully.");
                        setSnackbarSeverity("success");
                        return;
                    }
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    attempts++;
                }
                throw new Error('Timeout waiting for file to be ready');
            } else {
                throw new Error('Failed to get pre-signed URL');
            }
        } catch (error) {
            console.error(`Error exporting users: ${error}`);
            setSnackbarMessage("Error exporting users.");
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            onlyActive ? setIsExportingActive(false) : setIsExportingAll(false);
        }
    };

    const handleExportActiveUsers = () => {
        handleExportUsers(true);
    };

    const handleExportAllUsers = () => {
        handleExportUsers(false);
    };

    const handleImportCsv = () => {
        setImportCsvModalOpen(true);
    };

    const handleDeleteCsv = () => {
        setDeleteCsvModalOpen(true);
    };

    const handleUpdateEmailCsv = () => {
        setUpdateEmailCsvModalOpen(true);
    };

    const handleOpenAdminsModal = () => {
    setAdminFormState({
        assignAdmins: {
            admins: []
        }
    });
    setIsAdminsModalOpen(true);
    handleBulkMenuClose();
};
const handleCloseAdminsModal = () => {
    setIsAdminsModalOpen(false);
};

const handleAdminFormChange = (field, value) => {
    setAdminFormState(prev => ({
        ...prev,
        [field]: value
    }));
};

    const handleAdminsSave = async (adminData) => {
        try {
            let successMessage = '';

            // Only process if there are manually added admins
            if (adminData.manualAdminsCount > 0) {
                // Create all users first (batched)
                const userCreationPromises = adminFormState.assignAdmins.admins.map(admin => {
                    const user = {
                        firstName: admin.firstName,
                        lastName: admin.lastName,
                        userName: admin.email,
                        location: admin.location || '',
                        department: admin.department || '',
                        city: admin.city || '',
                        country: admin.country || '',
                        state: admin.state || '',
                        companyName: admin.companyName || '',
                        startUserJourneyOn: new Date().toISOString(),
                        tenancyId,
                        userSource: "MANUAL",
                        upn: null
                    };
                    return addUser({ user }).unwrap();
                });

                // Wait for all users to be created
                const createdUsers = await Promise.all(userCreationPromises);

                // Then promote all to admin (batched)
                const roleUpdatePromises = createdUsers.map(createdUser =>
                    updateUserRole({
                        userId: createdUser.id,
                        role: "TenancyAdministrators"
                    }).unwrap()
                );

                // Wait for all role updates to complete
                await Promise.all(roleUpdatePromises);

                successMessage = `${adminData.manualAdminsCount} administrator(s) have been added successfully.`;
            } else {
                successMessage = "Changes saved successfully.";
            }

            setSnackbarMessage(successMessage);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);

            setAdminFormState({
                assignAdmins: {
                    admins: []
                }
            });

        } catch (error) {
            console.error("Error processing administrators:", error);
            setSnackbarMessage("Error processing administrators. Please try again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleSendToTenancy = () => {
        setDialogTitle("Send Content Pack to Tenancy");
        setDialogContent(`Are you sure you want to send this content pack to all users in tenancy ${tenancyId}?`);
        setDialogAction(() => async () => {
            setIsSendingToTenancy(true);
            try {
                const contentPackData = {
                    contentPackId: contentPack.id,              
                    contentPackInternalId: contentPack.internalId,  
                    tenancyId: tenancyId
                };

                await sendTenancyContentPack(contentPackData).unwrap();
                setSnackbarMessage("Content pack sent to all tenancy users successfully.");
                setSnackbarSeverity("success");
            } catch (error) {
                console.error('Error sending content pack to tenancy:', error);
                setSnackbarMessage("Failed to send content pack to tenancy users.");
                setSnackbarSeverity("error");
            } finally {
                setIsSendingToTenancy(false);
                setSnackbarOpen(true);
            }
        });
        setDialogOpen(true);
    };

    const handleDeleteUsers = async () => {
        setLoading(true);
        setSelected([]);
        setDialogTitle("Delete Selected Users");
        setDialogContent("Are you sure you want to delete the selected users? This action is irreversible.");
        setDialogAction(() => async () => {
            try {
                await Promise.all(selected.map(id => deleteUser({ userId: id }).unwrap()));
                setSnackbarMessage(`${selected.length} user(s) deleted successfully.`);
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
                setSelected([]);
            } catch (error) {
                setSnackbarMessage("Error deleting users.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
            setLoading(false);
        });
        setDialogOpen(true);
    };

    const bulkMenuId = bulkMenuOpen ? "bulk-actions-menu" : undefined;

    const handleEdit = (user) => {
        if (type === "select") {
            return;
        }

        navigate(`/users/${user.id}`, { state: { user } });
    };

    const handleSendReminders = async () => {
        setIsSendingReminders(true);
        await sendTenancyReminderEmail({ tenancyId: tenancyId }).unwrap().then(() => {
            setSnackbarMessage("Reminders sent successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        }).catch(() => {
            setSnackbarMessage("Error sending reminders.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        });
        setIsSendingReminders(false);
    };

    const handleEnableChange = async (user) => {
        const tempUser = { ...user };
        tempUser.enabled = !tempUser.enabled;
        setUserLoading(prev => ({ ...prev, [user.id]: true }));

        await updateUser({ userId: user.id, user: tempUser }).unwrap().then(() => {
            setSnackbarMessage(`User has been ${tempUser.enabled ? "enabled" : "disabled"}.`);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        }).catch(() => {
            tempUser.enabled = !tempUser.enabled;
            setSnackbarMessage(`Error occurred while ${tempUser.enabled ? "enabling" : "disabling"} user.`);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        });

        setUserLoading(prev => ({ ...prev, [user.id]: false }));

        setSnackbarOpen(false);
    };

    const handleSend = (user) => {
        if (format === "contentPack") {
            const contentPackData = {
                contentPackId: contentPack.internalId,
                userIdentifier: user.email,
                userFirstName: user.firstName || "",
                userLastName: user.lastName || "",
                tenancyId: tenancyId,
                tenancyName: ""
            };

            sendManualContentPack(contentPackData).unwrap()
                .then(() => {
                    setSnackbarMessage(`Successfully sent content pack to user: ${user.email}`);
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                })
                .catch(() => {
                    setSnackbarMessage(`Failed to send content pack to user: ${user.email}`);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                });
        }
        else if (format === "course") {
            const courseData = {
                universityCourseId: formatId,
                userIdentifier: user.email,
                userFirstName: user.firstName || "",
                userLastName: user.lastName || "",
                tenancyId: tenancyId,
                tenancyName: ""
            };

            sendManualCourse(courseData).unwrap()
                .then(() => {
                    setSnackbarMessage(`Successfully sent course: ${formatId} to user: ${user.email}`);
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                })
                .catch(() => {
                    setSnackbarMessage(`Failed to send course: ${formatId} to user: ${user.email}`);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                });
        } else {
            const data = {
                TenancyId: tenancyId,
                ScheduleOption: "sendNow",
                ScheduleEmailDateTime: new Date(),
                SendType: format,
                SendTypeName: formatId,
                SendTo: "User",
                SimulationId: formatId,
                SendToId: 0,
                SendToName: user.email,
                SendToUserId: user.id,
                ScheduleDateTimeEnd: new Date(),
                SendingTimeStart: null,
                SendingTimeEnd: null,
                PauseWeekends: false,
                StopSending: false
            };

            createOnDemand({ data, tenancyId }).unwrap()
                .then(() => {
                    setSnackbarMessage(`Successfully sent content: ${formatId} to user: ${user.email}`);
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                })
                .catch(() => {
                    setSnackbarMessage(`Failed to send content: ${formatId} to user: ${user.email}`);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                });
        }
    };

    let headCells = [
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = type === "selectOne"
                    ? userIds.length === 1 && userIds[0].id === dataRow.id
                    : userIds.some(user => user.id === dataRow.id);
                return (
                    <IconButton
                        size="small"
                        disabled={!dataRow.enabled}
                        onClick={() => {
                            if (!dataRow.enabled) return;
                            
                            if (type === "selectOne") {
                                if (isSelected) {
                                    setUserId([]);
                                } else {
                                    setUserId([dataRow]);
                                }
                            } else {
                                if (isSelected) {
                                    setUserId(userIds.filter(user => user.id !== dataRow.id));
                                } else {
                                    setUserId([...userIds, dataRow]);
                                }
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon color={lightMode ? "black" : "white"} />}
                    </IconButton>
                );
            },
            initialVisible: type === "select" || type === "selectOne"
        },
        { id: "role", label: "Role", initialVisible: true },
        {
            id: "active",
            label: "Status",
            dataKey: "enabled",
            render: renderUserButton,
            initialVisible: true
        },
        {
            id: "edit",
            label: "Edit",
            render: (dataRow) => (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleEdit(dataRow)}
                    sx={{ textTransform: "capitalize" }}
                >
                    View
                </Button>
            ),
            initialVisible: type === "edit"
        },
        { id: "firstName", label: "First name", initialVisible: true },
        { id: "lastName", label: "Last name", initialVisible: true },
        { id: "email", label: "Email", initialVisible: true },
        { 
            id: "lineManagerName", 
            label: "Line Manager", 
            initialVisible: true,
            render: (dataRow) => {
                if (dataRow.lineManagerName && dataRow.lineManagerEmail) {
                    return `${dataRow.lineManagerName} (${dataRow.lineManagerEmail})`;
                }
                return '';
            }
        },
        { id: "department", label: "Department", initialVisible: true },
        { id: "location", label: "Location", initialVisible: true },
        {
            id: "userSource",
            label: "User Source",
            initialVisible: false,
            render: (dataRow) => resolveUserSourceLabel(dataRow.userSource)
        },
        {
            id: "groupNames",
            label: "Group",
            initialVisible: false,
            render: (dataRow) => dataRow.groupNames || ""
        },
        {
            id: "dateCreated",
            label: "Date created",
            initialVisible: false,
            render: (dataRow) => {
                const date = new Date(dataRow.dateCreated);
                const formattedDate = date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                return formattedDate;
            }
        },
        {
            id: "lastLoginDate",
            label: "Last logged in",
            initialVisible: false,
            render: (dataRow) => {
                if (!dataRow.lastLoginDate) {
                    return 'Never';
                }
                const date = new Date(dataRow.lastLoginDate);
                const formattedDate = date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                return formattedDate;
            }
        },
        { id: "city", label: "City", initialVisible: false },
        { id: "state", label: "State", initialVisible: false },
        { id: "country", label: "Country", initialVisible: false },
        { id: "companyName", label: "Company name", initialVisible: false }
    ];

    const toolbarActions = [
        {
            render: () => (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", rowGap: 1 }}>
                    {type === "edit" && (
                        <>
                            {/* Own flex group so filters wrap as a unit, separate from the actions group below */}
                            {isRoleColumnVisible() && (
                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                                    <FormControl>
                                        <InputLabel style={{ backgroundColor: "transparent" }}>Status</InputLabel>
                                        <Select
                                            variant="outlined"
                                            size="small"
                                            sx={{ mr: 2, minWidth: 90, '& .MuiSelect-select': { pr: 4 } }}
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            label="Status"
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="Enabled">Enabled</MenuItem>
                                            <MenuItem value="Disabled">Disabled</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <InputLabel style={{ backgroundColor: "transparent" }}>Role</InputLabel>
                                        <Select
                                            variant="outlined"
                                            size="small"
                                            sx={{ mr: 2, minWidth: 120, '& .MuiSelect-select': { pr: 4 } }}
                                            value={roleFilter}
                                            onChange={(e) => setRoleFilter(e.target.value)}
                                            label="Role"
                                        >
                                            <MenuItem value="All">All Roles</MenuItem>
                                            {isAdmin && <MenuItem value="Administrators">Super Admin</MenuItem>}
                                            <MenuItem value="TenancyAdministrators">Tenancy Administrator</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl>
                                        <InputLabel style={{ backgroundColor: "transparent" }}>Source</InputLabel>
                                        <Select
                                            id="source-filter-select"
                                            variant="outlined"
                                            size="small"
                                            sx={{ mr: 2, minWidth: 130, '& .MuiSelect-select': { pr: 4 } }}
                                            value={sourceFilter}
                                            onChange={(e) => setSourceFilter(e.target.value)}
                                            label="Source"
                                        >
                                            <MenuItem value="All">All Sources</MenuItem>
                                            {Object.entries(USER_SOURCE_LABELS).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}

                            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                            <Button
                                size="medium"
                                variant="contained"
                                sx={{ textTransform: "capitalize", mr: 1 }}
                                aria-controls={bulkMenuId}
                                aria-haspopup="true"
                                onClick={handleBulkMenuClick}
                                endIcon={<AddIcon />}
                            >
                                Bulk actions
                            </Button>
                            <Menu
                                id={bulkMenuId}
                                anchorEl={bulkMenuAnchorEl}
                                open={bulkMenuOpen}
                                onClose={handleBulkMenuClose}
                                MenuListProps={{
                                    "aria-labelledby": "add-user-button",
                                    sx: { bgcolor: "background.default" }
                                }}
                            >
                                <MenuItem onClick={handleExportActiveUsers} disabled={isExportingActive}>
                                    <IosShareIcon sx={{ mr: 1 }} />
                                    {isExportingActive ? 'Exporting...' : 'Export Active Users'}
                                </MenuItem>
                                <MenuItem onClick={handleExportAllUsers} disabled={isExportingAll}>
                                    <IosShareIcon sx={{ mr: 1 }} />
                                    {isExportingAll ? 'Exporting...' : 'Export All Users'}
                                </MenuItem>
                                {/* Kept as a direct child of Menu: wrapping it in Tooltip/span breaks
                                    MenuList focus handling, and a disabled MenuItem swallows the hover
                                    a Tooltip needs anyway. The reason goes in the label instead. */}
                                <MenuItem onClick={handleSendReminders} disabled={isSendingReminders || emailsPaused}>
                                    {isSendingReminders ? (
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                    ) : (
                                        <NotificationsNoneIcon sx={{ mr: 1 }} />
                                    )}
                                    {isSendingReminders ? "Sending..." : (emailsPaused ? "Send Reminders (emails paused)" : "Send Reminders")}
                                </MenuItem>

                                 <MenuItem onClick={handleOpenAdminsModal}>
                                    <SupervisorAccountIcon sx={{ mr: 1 }} />
                                     Assign Admins
                                 </MenuItem>

                                <MenuItem onClick={handleImportCsv}>
                                    <SystemUpdateAltIcon sx={{ mr: 1 }} />
                                    Add Using CSV
                                </MenuItem>
                                <MenuItem onClick={handleDeleteCsv}>
                                    <DeleteOutlineIcon sx={{ mr: 1 }} />
                                    Delete Using CSV
                                </MenuItem>
                                {(isAdmin || isTenancyAdmin) && (
                                    <MenuItem onClick={handleUpdateEmailCsv}>
                                        <EmailIcon sx={{ mr: 1 }} />
                                        Update Emails Using CSV
                                    </MenuItem>
                                )}
                            </Menu>
                            <Button
                                size="medium"
                                variant="contained"
                                sx={{ ml: 1 }}
                                onClick={handleAddUserManually}
                                endIcon={<AddIcon />}
                            >
                                Add User
                            </Button>
                            <Tooltip title="Select users to delete" placement="top" arrow>
                                <Button
                                    size="medium"
                                    variant="contained"
                                    color="warning"
                                    sx={{ ml: 2 }}
                                    onClick={handleDeleteUsers}
                                    disabled={selected.length === 0 && !loading}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Delete"}
                                </Button>
                            </Tooltip>
                            </Box>
                        </>
                    )}

                    {/* Send to Tenancy Button - only show for content pack format in send mode */}
                    {type === "send" && format === "contentPack" && (
                        <Button
                            size="medium"
                            variant="contained"
                            color="primary"
                            onClick={handleSendToTenancy}
                            disabled={isSendingToTenancy}
                            sx={{ ml: 2 }}
                        >
                            {isSendingToTenancy ? (
                                <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                            ) : (
                                "Send to Tenancy"
                            )}
                        </Button>
                    )}
                </Box>
            )
        }
    ];

    if (type === "send") {
        headCells.push({
            id: "send",
            label: "Send",
            render: (dataRow) => (
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleSend(dataRow)}
                    disabled={!dataRow.enabled}
                >
                    Send to User
                </Button>
            ),
            initialVisible: true
        });
    }

    useEffect(() => {
        if (visibleColumns.length === 0) {
            setVisibleColumns(headCells.filter(cell => cell.initialVisible).map(cell => cell.label));
        }
    }, []);

    useEffect(() => {
        if (!isRoleColumnVisible()) {
            setStatusFilter("All");
            setRoleFilter("All");
            setSourceFilter("All");
        }
    }, [visibleColumns]);

    useEffect(() => {
        if (type === "selectOne" && selected.length > 1) {
            const [firstSelected] = selected;
            setSelected([firstSelected]);
            setUserId(userIds.filter(user => user.id === firstSelected));
        }
    }, [type, selected, setUserId, userIds]);

    useEffect(() => {
        if (type === "filter" && usersData) {
            setFilteredUsersData(usersData?.data.filter(user => user[filterBy] === parseInt(filterId)));
        }
    }, [usersData, filterBy, filterId, type]);

    return (
        <>
            <Table
                name={"Users"}
                data={getFilteredUsers()}
                error={usersError}
                loading={usersLoading}
                headCells={headCells}
                search={true}
                editColumn={type === "edit"}
                checkbox={type === "edit"}
                onCellClick={handleEdit}
                toolbarActions={!matchDownMD ? toolbarActions : []}
                showToolbarActions={true}
                onDelete={handleDeleteUsers}
                selected={selected}
                setSelected={setSelected}
                maxTableHeight={type === "select" ? "calc(100vh - 400px)" : "calc(100vh - 320px)"}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                count={totalUsers}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                isServerSidePagination={true}
                getChangedData={getChangedData}
                serverSearchQuery={serverSearchQuery}
                setServerSearchQuery={setServerSearchQuery}
                plural={'users'}
                enableSorting={false}
                isRowSelectable={(row) => row.enabled}
                onVisibleColumnsChange={(newVisibleColumns) => {
                    setVisibleColumns(newVisibleColumns);
                }}
                visibleColumns={visibleColumns}
            />

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <AddUserModal
                open={addUserModalOpen}
                onClose={() => setAddUserModalOpen(false)}
            />
            <ImportCsvModal
                open={importCsvModalOpen}
                onClose={() => {
                    setImportCsvModalOpen(false);
                    handleBulkMenuClose();
                }}
            />
            <DeleteCsvModal
                open={deleteCsvModalOpen}
                onClose={() => {
                    setDeleteCsvModalOpen(false);
                    handleBulkMenuClose();
                }}
            />
            <UpdateEmailCsvModal
                open={updateEmailCsvModalOpen}
                onClose={() => {
                    setUpdateEmailCsvModalOpen(false);
                    handleBulkMenuClose();
                }}
            />
            <ConfirmationDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => setDialogOpen(false)}
            />

           <AssignAdminModal
             open={isAdminsModalOpen}
             onClose={handleCloseAdminsModal}
             state={adminFormState}
             onChange={handleAdminFormChange}
             onSave={handleAdminsSave}
           />
        </>
    );
}